import jsPDF from "jspdf";
import assetUrl from '../../services/assetUrl';

// NCET-branded application report. Built with plain jsPDF primitives (no
// jspdf-autotable dependency) so it works with the installed jspdf v3.

const NAVY = [14, 36, 85];
const ORANGE = [246, 135, 42];
const LIGHT = [240, 244, 250];
const BORDER = [205, 215, 230];
const TEXT = [40, 40, 40];
const LOGO_URL = assetUrl("/ncet_assets/images/NGE-Logo.png");

const loadImageAsBase64 = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const val = (v) => (v !== undefined && v !== null && String(v).trim() !== "" ? String(v).trim() : "â€”");

export const downloadReport = async (formData, rectype) => {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 14; // page margin
  const labelW = 62;

  const submittedAt = new Date().toLocaleString("en-IN", {
    day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
  });

  const logo = await loadImageAsBase64(LOGO_URL);
  const HEADER_H = 34;

  //  Header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, HEADER_H, "F");
  if (logo) {
    try { doc.addImage(logo, "PNG", M, 6, 22, 22); } catch { /* skip */ }
  }
  const textX = logo ? M + 28 : M;
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("Nagarjuna College of Engineering & Technology", textX, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text(`${rectype} Recruitment â€” Application Report`, textX, 20);
  doc.setFontSize(8);
  doc.setTextColor(190, 210, 245);
  doc.text("www.ncet.co.in", textX, 27);

  //  Orange accent line
  doc.setFillColor(...ORANGE);
  doc.rect(0, HEADER_H, pageW, 1.2, "F");

  //  Meta box
  let y = HEADER_H + 8;
  doc.setFillColor(...LIGHT);
  doc.roundedRect(M, y, pageW - M * 2, 16, 2, 2, "F");
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.roundedRect(M, y, pageW - M * 2, 16, 2, 2, "S");
  doc.setTextColor(...TEXT);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text(`Submission Date & Time:  ${submittedAt}`, M + 4, y + 6);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(`Application Reference ID:  ${val(formData.Applicationid)}`, M + 4, y + 12);
  y += 24;

  const ensureSpace = (needed) => {
    if (y + needed > pageH - 16) {
      doc.addPage();
      y = 16;
    }
  };

  const section = (title, rows) => {
    const filled = rows.filter(([, v]) => v !== undefined);
    if (!filled.length) return;
    ensureSpace(16);
    // section header bar
    doc.setFillColor(...NAVY);
    doc.rect(M, y, pageW - M * 2, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.text(title, M + 3, y + 5.5);
    y += 8;

    doc.setFontSize(9);
    filled.forEach(([label, value], i) => {
      const text = val(value);
      const valueW = pageW - M * 2 - labelW - 6;
      const lines = doc.splitTextToSize(text, valueW);
      const rowH = Math.max(8, lines.length * 4.6 + 3);
      ensureSpace(rowH);
      // zebra background
      doc.setFillColor(...(i % 2 === 0 ? [248, 250, 253] : [255, 255, 255]));
      doc.rect(M, y, pageW - M * 2, rowH, "F");
      // label cell
      doc.setFillColor(...LIGHT);
      doc.rect(M, y, labelW, rowH, "F");
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.2);
      doc.rect(M, y, pageW - M * 2, rowH, "S");
      doc.line(M + labelW, y, M + labelW, y + rowH);
      // text
      doc.setTextColor(...NAVY);
      doc.setFont("helvetica", "bold");
      doc.text(doc.splitTextToSize(label, labelW - 5), M + 3, y + 5.2);
      doc.setTextColor(...TEXT);
      doc.setFont("helvetica", "normal");
      doc.text(lines, M + labelW + 3, y + 5.2);
      y += rowH;
    });
    y += 6;
  };

  if (rectype === "Non Teaching") {
    section("Personal Details", [
      ["Full Name", formData.fullname], ["Date of Birth", formData.dob], ["Gender", formData.gender],
      ["Mobile Number", formData.phone], ["Email ID", formData.email], ["Address", formData.address],
    ]);
    section("Post Applied & Education", [
      ["Post Applied For", formData.selectpost], ["Highest Qualification", formData.qualification],
      ["Specialization", formData.nt_specialization], ["Institution / University", formData.nt_institution],
      ["Year of Passing", formData.nt_yearpass], ["Percentage / CGPA", formData.nt_cgpa],
    ]);
    section("Experience", [
      ["Has Experience", formData.has_experience], ["Total Experience (yrs)", formData.total_exp],
      ["Previous Organization", formData.prev_org],
    ]);
  }

  if (rectype === "Teaching") {
    section("Position Applied", [
      ["Applying For (School)", formData.selcollege], ["Department", formData.selDep],
      ["Specialization", formData.selDepSpc], ["Post Type", formData.selectpost],
    ]);
    section("Personal Details", [
      ["Full Name", formData.fullname], ["Email", formData.email], ["Mobile Number", formData.phone],
      ["Alternate Mobile", formData.altphone], ["Date of Birth", formData.dob], ["Address", formData.address],
      ["City", formData.city], ["District", formData.dist], ["State", formData.state], ["Pincode", formData.pincode],
    ]);
    section("Ph.D Details", [
      ["Ph.D Status", formData.phdstatus], ["Year of Award", formData.phdpassyear],
      ["Department", formData.phdDept],
      ["Thesis Title", formData.phdThesis],
      ["University", formData.phdUniversity],
    ]);
    section("PG Details", [
      ["PG Degree", formData.selectpg], ["Branch", formData.pg_branch],
      ["Other PG", formData.pg_other], ["Specialization", formData.specialization], ["Percentage / CGPA", formData.CGPA],
      ["Year of Passing", formData.pgpass], ["Institution", formData.institution_pg], ["University", formData.university_pg],
      ["GATE / GPAT Rank", formData.gaterank],
    ]);
    section("UG Details", [
      ["UG Degree", formData.selectug], ["Other UG", formData.ug_other], ["Specialization", formData.ugspecialization],
      ["Percentage / CGPA", formData.ugCGPA], ["Year of Passing", formData.ugpass], ["Institution", formData.institution_ug],
      ["University", formData.university_ug], ["EAMCET / ECET Rank", formData.eamcet_rank],
    ]);
    section("Experience & Salary", [
      ["Industrial Experience (yrs)", formData.industrial_exp], ["Academic Experience (yrs)", formData.academic_exp],
      ["Total Experience (yrs)", formData.total_exp], ["Current Salary", formData.current_sal], ["Expected Salary", formData.expected_sal],
    ]);
    section("Research & Publications", [
      ["National Journal Papers", formData.njournals_count], ["National Conference Papers", formData.nconference_count],
      ["International Journal Papers", formData.injournals_count], ["International Conference Papers", formData.inconference_count],
      ["Scopus ID", formData.scopus_id], ["ORCID ID", formData.orcid_id], ["Books & Book Chapters", formData.books_published],
      ["Patents", formData.patents], ["Scopus Journals", formData.scopus_journals], ["SCI Journals", formData.sci_journals],
    ]);
  }

  //  Footer on every page
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFillColor(...NAVY);
    doc.rect(0, pageH - 11, pageW, 11, "F");
    doc.setFontSize(7.5);
    doc.setTextColor(190, 210, 245);
    doc.setFont("helvetica", "normal");
    doc.text("Â© Nagarjuna College of Engineering & Technology â€” All Rights Reserved", pageW / 2, pageH - 4, { align: "center" });
    doc.text(`Page ${i} of ${pages}`, pageW - M, pageH - 4, { align: "right" });
  }

  doc.save(`NCET_Application_${val(formData.Applicationid)}.pdf`);
};

export default downloadReport;
